import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, AlertCircle, Loader2 } from "lucide-react";

import Step1Business, { validateStep1 } from "./CreatePartnerSteps/Step1_Business";
import Step2Contact, { validateStep2 } from "./CreatePartnerSteps/Step2_Contact";
import Step3Address, { validateStep3 } from "./CreatePartnerSteps/Step3_Address";
import Step4Operations, { validateStep4 } from "./CreatePartnerSteps/Step4_Operations";
import Step5Fleet, { validateStep5, emptyVehicle } from "./CreatePartnerSteps/Step5_Fleet";
import Step6Account, { validateStep6 } from "./CreatePartnerSteps/Step6_Account";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: "Business Info" },
  { id: 2, title: "Contact Details" },
  { id: 3, title: "Address & Area" },
  { id: 4, title: "Operations Profile" },
  { id: 5, title: "Vehicle Fleet" },
  { id: 6, title: "Account Setup" },
];

const VALIDATORS = {
  1: validateStep1,
  2: validateStep2,
  3: validateStep3,
  4: validateStep4,
  5: validateStep5,
  6: validateStep6,
};

const initialFormData = {
  // step 1
  partner_type: "NETWORK",
  business_name: "",
  owner_name: "",
  business_type: "",
  gstin: "",
  pan: "",
  msme_number: "",
  years_in_business: "",
  // step 2
  primary_mobile: "",
  alternate_mobile: "",
  whatsapp_number: "",
  email: "",
  poc_name: "",
  poc_mobile: "",
  // step 3
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
  latitude: "",
  longitude: "",
  service_area_pincodes: [],
  // step 4
  num_employees: "",
  avg_monthly_shipments: "",
  current_clients: "",
  operating_hours: "",
  has_warehouse: false,
  warehouse_sqft: "",
  willing_to_handle: [],
  // step 5
  vehicles: [emptyVehicle()],
  // step 6
  password: "",
  commission_pct: "",
  settlement_cycle: "",
  settlement_n_months: "",
  settlement_day: "",
  inflation_min_pct: 25,
  inflation_max_pct: 100,
  report_download_partner: false,
  report_download_staff: false,
  wallet_type: "PREPAID",
  credit_limit: "",
  credit_wallet_enabled: true,
  network_partner_id: "",
  network_commission_enabled: false,
  network_commission_pct: "",
};

export default function CreatePartner() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [errorsByStep, setErrorsByStep] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  // Cross-step validation summary (for badges on step indicator)
  const errorCounts = useMemo(() => {
    const counts = {};
    Object.entries(VALIDATORS).forEach(([step, fn]) => {
      const errs = fn(formData);
      counts[step] = Object.keys(errs).length;
    });
    return counts;
  }, [formData]);

  const handleNext = () => {
    const errs = VALIDATORS[currentStep](formData);
    if (Object.keys(errs).length > 0) {
      setErrorsByStep((prev) => ({ ...prev, [currentStep]: errs }));
      toast.error(`Please fix ${Object.keys(errs).length} error(s) to continue`);
      return;
    }
    setErrorsByStep((prev) => ({ ...prev, [currentStep]: {} }));
    if (currentStep < 6) setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    // Validate all steps
    const allErrors = {};
    let firstErrorStep = null;
    for (const [step, fn] of Object.entries(VALIDATORS)) {
      const errs = fn(formData);
      if (Object.keys(errs).length) {
        allErrors[step] = errs;
        if (firstErrorStep === null) firstErrorStep = Number(step);
      }
    }
    setErrorsByStep(allErrors);
    if (firstErrorStep) {
      setCurrentStep(firstErrorStep);
      toast.error(
        `Errors found in step ${firstErrorStep}. Please review highlighted fields.`
      );
      return;
    }

    setSubmitting(true);
    setApiError("");
    try {
      const token = localStorage.getItem("sendtoo_token");
      const validVehicles = (formData.vehicles || []).filter(
        (v) => v.vehicle_number?.trim() || v.vehicle_type
      );

      const payload = {
        partner_type: formData.partner_type,
        business_name: formData.business_name,
        owner_name: formData.owner_name,
        business_type: formData.business_type,
        gstin: formData.gstin || null,
        pan: formData.pan,
        msme_number: formData.msme_number || null,
        years_in_business: formData.years_in_business || null,

        primary_mobile: formData.primary_mobile,
        alternate_mobile: formData.alternate_mobile || null,
        whatsapp_number: formData.whatsapp_number || formData.primary_mobile,
        email: formData.email,
        password: formData.password,
        poc_name: formData.poc_name || null,
        poc_mobile: formData.poc_mobile || null,

        address_line1: formData.address_line1,
        address_line2: formData.address_line2 || null,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        latitude: formData.latitude || null,
        longitude: formData.longitude || null,
        service_area_pincodes: formData.service_area_pincodes || [],

        num_employees: formData.num_employees || null,
        avg_monthly_shipments: formData.avg_monthly_shipments || null,
        current_clients: formData.current_clients || null,
        operating_hours: formData.operating_hours || null,
        has_warehouse: !!formData.has_warehouse,
        warehouse_sqft: formData.has_warehouse ? formData.warehouse_sqft || null : null,
        willing_to_handle: formData.willing_to_handle || [],

        vehicles: validVehicles.map((v) => ({
          vehicle_number: v.vehicle_number,
          vehicle_type: v.vehicle_type,
          capacity_kg: Number(v.capacity_kg || 0),
          ownership: v.ownership || "OWN",
        })),

        commission_pct: formData.commission_pct === "" ? null : Number(formData.commission_pct),
        settlement_cycle: formData.settlement_cycle || null,
        settlement_n_months:
          formData.settlement_cycle === "MULTI_MONTH"
            ? Number(formData.settlement_n_months || 0) || null
            : null,
        settlement_day: formData.settlement_day === "" ? null : Number(formData.settlement_day),
        inflation_min_pct: Number(formData.inflation_min_pct ?? 25),
        inflation_max_pct: Number(formData.inflation_max_pct ?? 100),
        report_download_partner: !!formData.report_download_partner,
        report_download_staff: !!formData.report_download_staff,
        wallet_type: formData.wallet_type || "PREPAID",
        credit_limit:
          (formData.wallet_type || "PREPAID") === "CREDIT"
            ? Number(formData.credit_limit || 0)
            : 0,
        credit_wallet_enabled:
          (formData.wallet_type || "PREPAID") === "CREDIT"
            ? formData.credit_wallet_enabled !== false
            : false,

        network_partner_id:
          formData.partner_type === "REVENUE" || formData.partner_type === "EXECUTION"
            ? formData.network_partner_id || null
            : null,
        network_commission_enabled: !!formData.network_commission_enabled,
        network_commission_pct:
          formData.network_commission_enabled && formData.network_commission_pct !== ""
            ? Number(formData.network_commission_pct)
            : null,
      };

      const r = await fetch(`${API}/api/v2/partners`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = data?.detail || data?.message || `Request failed (${r.status})`;
        setApiError(typeof msg === "string" ? msg : JSON.stringify(msg));
        toast.error("Failed to create partner");
        return;
      }
      const code = data.partner_code || data.code || data.id || "—";
      toast.success(
        `Partner ${formData.business_name} created!  Partner Code: ${code}`,
        { duration: 4000 }
      );
      setTimeout(() => navigate("/admin/partners"), 2000);
    } catch (e) {
      setApiError(e.message || "Network error");
      toast.error("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  const stepProps = {
    formData,
    setFormData,
    errors: errorsByStep[currentStep] || {},
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="create-partner-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <button
              onClick={() => navigate("/admin/partners")}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-1.5 transition"
              data-testid="back-to-partners-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Partners
            </button>
            <h1 className="text-2xl font-bold text-slate-800">Add New Partner</h1>
            <p className="text-sm text-slate-500">
              Complete all sections to activate partner account
            </p>
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator
          steps={STEPS}
          currentStep={currentStep}
          errorCounts={errorCounts}
          onJump={(id) => setCurrentStep(id)}
        />

        {/* Content card */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 md:p-7">
          {apiError && currentStep === 6 && (
            <div
              className="mb-5 flex items-start gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700"
              data-testid="api-error-banner"
            >
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{apiError}</span>
            </div>
          )}
          {currentStep === 1 && <Step1Business {...stepProps} />}
          {currentStep === 2 && <Step2Contact {...stepProps} />}
          {currentStep === 3 && <Step3Address {...stepProps} />}
          {currentStep === 4 && <Step4Operations {...stepProps} />}
          {currentStep === 5 && <Step5Fleet {...stepProps} />}
          {currentStep === 6 && <Step6Account {...stepProps} />}
        </div>

        {/* Bottom Navigation */}
        <div
          className="sticky bottom-0 bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3 flex items-center justify-between"
          data-testid="step-nav"
        >
          <div className="w-32">
            {currentStep > 1 && (
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={submitting}
                data-testid="step-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="text-sm text-slate-500" data-testid="step-counter">
            Step {currentStep} of 6
          </div>
          <div className="w-44 flex justify-end">
            {currentStep < 6 ? (
              <Button
                onClick={handleNext}
                className="bg-slate-800 hover:bg-slate-900 text-white"
                disabled={submitting}
                data-testid="step-next-btn"
              >
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="create-partner-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1" /> Create Partner
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function StepIndicator({ steps, currentStep, errorCounts, onJump }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-3 md:p-4"
      data-testid="step-indicator"
    >
      <div className="flex items-center">
        {steps.map((step, idx) => {
          const isActive = step.id === currentStep;
          const isCompleted = step.id < currentStep;
          const errCount = errorCounts[step.id] || 0;
          return (
            <div key={step.id} className="flex items-center flex-1 last:flex-none">
              <button
                type="button"
                onClick={() => onJump(step.id)}
                className="flex items-center gap-2 group"
                data-testid={`step-${step.id}-button`}
              >
                <div className="relative">
                  <div
                    className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center text-xs font-bold transition ${
                      isActive
                        ? "bg-slate-800 text-white ring-4 ring-slate-100"
                        : isCompleted
                        ? "bg-emerald-600 text-white"
                        : "bg-slate-100 text-slate-400 border border-slate-200"
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <span>{step.id}</span>
                    )}
                  </div>
                  {errCount > 0 && step.id !== currentStep && (
                    <span
                      className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center"
                      data-testid={`step-${step.id}-error-badge`}
                    >
                      {errCount}
                    </span>
                  )}
                </div>
                <span
                  className={`hidden md:block text-xs font-semibold ${
                    isActive
                      ? "text-slate-800"
                      : isCompleted
                      ? "text-emerald-700"
                      : "text-slate-400"
                  }`}
                >
                  {step.title}
                </span>
              </button>
              {idx < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 md:mx-3 ${
                    step.id < currentStep ? "bg-emerald-500" : "bg-slate-200"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
